import React, { useState, useEffect, useCallback } from "react";
import { jwtFetch } from "./utils/apiClient";
import { useParams } from "react-router-dom";
import { format, subDays, addDays } from "date-fns";
import { config, priceFormat } from "./Components/Config";
import Menu from "./Components/Menu";
import Header from "./Components/Header";

const InvoiceRequestList = () => {
	const [invoiceRequests, setInvoiceRequests] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [type, setType] = useState('upcoming');
	const [days, setDays] = useState(0);

	const params = useParams();
	const types = params.type;

		const getInvoiceRequests = useCallback(async () => {
			try {
				setIsLoading(true);
				const requestInvoiceRequests = {};
				const fetchInvoiceRequests = await jwtFetch(config.ENDPOINT + 'get/iou', {
					method: 'POST',
					headers: {
						"Content-Type": "application/json",
					},
					body: JSON.stringify(requestInvoiceRequests)
				});
				const responseGetInvoiceRequests = await fetchInvoiceRequests.json();
				if (responseGetInvoiceRequests.status === 200) {
					setInvoiceRequests(responseGetInvoiceRequests.data);
				} else {
					alert(responseGetInvoiceRequests.message);
				}
			} catch (error) {
				console.error('Error in getInvoiceRequests:', error);
				alert('An error occurred: ' + (error.message || error));
			} finally {
				setIsLoading(false);
			}
		}, []);

		useEffect(() => {
		const typeText = types.split('-');
		setType(typeText[0]);
		setDays(typeText[1]);
		getInvoiceRequests();
		}, [types, getInvoiceRequests]);

	const filteredInvoiceRequests = invoiceRequests
		.filter((invoiceRequest) => {
			const today = new Date();
			today.setHours(0, 0, 0, 0);
			const startDate = new Date(invoiceRequest.start_term_period);
			startDate.setHours(0, 0, 0, 0);

			if (['Canceled', 'Stopped'].includes(invoiceRequest.iou_status)) {
				return false;
			}

			if (type === 'upcoming') {
				if (invoiceRequest.payment_paid_date) {
					return false;
				}
				const daysFromNow = addDays(today, parseInt(days, 10));
				return startDate >= today && startDate <= daysFromNow;
			}

			if (type === 'past') {
				if (invoiceRequest.payment_paid_date) {
					return false;
				}
				const daysAgo = subDays(today, parseInt(days, 10));
				return startDate <= today && startDate >= daysAgo;
			}

			return true;
		})
		.sort((a, b) => {
			if (new Date(a.start_term_period) - new Date(b.start_term_period) === 0) {
				return b.iou_subtotal - a.iou_subtotal;
			}
			return new Date(a.start_term_period) - new Date(b.start_term_period);
		});

	return (
		<div id="report">
			<div className="container-fluid p-0">
				<div className="row g-0 mh-100vh">
					<div className="col-md-2 border-end">
						<div className="d-md-flex d-none align-items-center justify-content-center p-1 h-48">
							<a className="h-100" href="/dashboard"><img className="h-100" src="/cuancuan.png" alt="PointStar" /></a>
						</div>
						<hr className="m-0" />
						<Menu active="dashboard" />
					</div>
					<div className="col-md-10">
						<Header />
						<hr className="m-0" />
						<div className="main">
							<div className="p-4">

								<section className="mb-4">
									<div className="d-flex align-items-center justify-content-between mb-4">
										<div className="fs-24">{type === 'upcoming' ? 'Upcoming Invoice Report (Next ' : 'Unpaid Invoice Report (Past '} {days} {days === 1 ? 'day)' : 'days)'}</div>
									</div>
									<div className="bg-white border-radius-4 p-4">
										{isLoading ? (
											<p>{isLoading ? 'Loading...' : 'No data'}</p>
										) : (
											<React.Fragment>
												<div className="row mb-2">
													<div className="col-md-4 text-secondary">Total Invoice Request</div>
													<div className="col-md-8">{filteredInvoiceRequests.length}</div>
												</div>
												<div className="row mb-4">
													<div className="col-md-4 text-secondary">Total Amount</div>
													<div className="col-md-8">{priceFormat(filteredInvoiceRequests.reduce((total, invoiceRequest) => total + parseFloat(invoiceRequest.iou_subtotal), 0).toFixed(3), 'IDR')}</div>
												</div>
												<div className="table-responsive mb-2" style={{ maxHeight: 'calc(100vh - 230px)', overflow: 'auto' }}>
													<table id="table-data" className="table table-hover mb-0" style={{ tableLayout: 'auto', width: '100%' }}>
														<thead style={{ position: 'sticky', top: 0, zIndex: 3 }}>
															<tr>
																<th className="bg-gold w-40 text-nowrap">#</th>
																<th className="bg-gold text-nowrap">QT Number</th>
																<th className="bg-gold text-nowrap">SO Number</th>
																<th className="bg-gold text-nowrap">Company Group</th>
																<th className="bg-gold text-nowrap">Subsidiary</th>
																<th className="bg-gold text-nowrap">Sales Name</th>
																<th className="bg-gold text-nowrap">Schedule</th>
																<th className="bg-gold text-nowrap">Period Start Date</th>
																<th className="bg-gold text-nowrap">Period End Date</th>
																<th className="bg-gold text-nowrap">IR Status</th>
																<th className="bg-gold text-nowrap">IR Number</th>
																<th className="bg-gold text-nowrap">Invoice Number</th>
																<th className="bg-gold text-nowrap text-end">Total</th>
															</tr>
														</thead>
														<tbody>
															{
																filteredInvoiceRequests.map((invoiceRequest, i) => (
																	<tr key={`invoice-request-${i}`}>
																		<td className="w-40 text-nowrap" valign="top">{i + 1}</td>
																		<td className="text-nowrap" valign="top"><a href={`/quotation/detail/${invoiceRequest.quotation_id}`} target="_blank" rel="noreferrer">{invoiceRequest.quotation_code}</a></td>
																		<td className="text-nowrap" valign="top"><a href={`/salesorder/detail/${invoiceRequest.so_id}`} target="_blank" rel="noreferrer">{invoiceRequest.so_code}</a></td>
																		<td className="text-nowrap" valign="top">{invoiceRequest.company_name}</td>
																		<td className="text-nowrap" valign="top">{invoiceRequest.subcompany_billing_name}</td>
																		<td className="text-nowrap" valign="top">{invoiceRequest.sales_name}</td>
																		<td className="text-nowrap" valign="top">{invoiceRequest.term_number.replace('Term ', 'Schedule ')}</td>
																		<td className="text-nowrap text-center" valign="top">{format(new Date(invoiceRequest.start_term_period), 'd MMM yyyy')}</td>
																		<td className="text-nowrap text-center" valign="top">{format(new Date(invoiceRequest.end_term_period), 'd MMM yyyy')}</td>
																		<td className="text-nowrap text-center" valign="top">{invoiceRequest.iou_status}</td>
																		<td className="text-nowrap" valign="top"><a href={`/invoicerequest/detail/${invoiceRequest.id_iou}`} target="_blank" rel="noreferrer">{invoiceRequest.iou_code}</a></td>
																		<td className="text-nowrap" valign="top"><a href={`/invoicerequest/detail/${invoiceRequest.id_iou}`} target="_blank" rel="noreferrer">{invoiceRequest.invoice_number}</a></td>
																		<td className="text-nowrap text-end" valign="top">{priceFormat(invoiceRequest.iou_subtotal, 'IDR')}</td>
																	</tr>
																))
															}
														</tbody>
													</table>
												</div>
											</React.Fragment>
										)}
									</div>
								</section>

							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default InvoiceRequestList;


